<!--
© Copyright 2015-2026 Personalized Privacy Assistant Project, Carnegie Mellon University - All Rights Reserved - US Patent 11,768,949, US Patent 10,956,586, US Patent 12,235,973, and additional USPTO and EPO patents pending.
This code is proprietary and confidential. For any questions about this code, including licensing, please contact Prof. Norman Sadeh (sadeh@cs.cmu.edu).
-->
<template>
  <div>
    <div class="container">
      <div class="row">

        <!-- route == 'edit' -->
        <div class="col-md-12" v-if="route == 'edit'">
          <irr-form-component :route="route" :csrf_token="csrf_token" :errors="errors" :existingData="existingData"></irr-form-component>
        </div>

        <div class="col-md-12" v-if="route == 'index'">
          <div class="row">
            <div v-if="statusMessage" class="col-md-12">
              <div class="alert alert-success" role="alert">{{ statusMessage }}</div>
            </div>
            <div class="col-md-12">
              <multi-irr-summary-component :irrInfo="irrInfo"></multi-irr-summary-component>
            </div>
            <div class="col-md-12">
              <div class="card shadow overflow-hidden">
                <irr-map-component :route="route" :irrs="results.data" :activeIrrId="activeIrrId" :existingData="existingData"></irr-map-component>
              </div>
            </div>
            <div class="col-md-12">
              <irr-full-list-component :route="route" v-on:change-active-irr="changeActiveIrr" :results="results" :resourceFramework="resourceFramework" :specific-user="specificUser" :permissions="permissions" :filter="filter" :paginateHtml="paginateHtml" :irrInfo="irrInfo"></irr-full-list-component>
            </div>
          </div>
        </div>

        <!-- route == 'show' -->
        <div class="col-md-12" v-if="route == 'show'">
          <div class="container d-flex flex-column">
            <div class="row">
              <div class="col-md-6">
                <irr-summary-component :permissions="permissions" :irr="irr" :resources="resources" :base-urls="baseUrls" :csrf_token="csrf_token" :num-users="numUsers" :num-pending-publish-requests="numPendingPublishRequests" :ownership-transfer="ownershipTransfer" :lifecycle="lifecycle"></irr-summary-component>
              </div>
              <div class="col-md-6">
                <irr-resource-map-component :irr="irr" :resources="resources" :num-resources-to-display="numResourcesToDisplay"></irr-resource-map-component>
              </div>
            </div>
            <div class="mt-5">
                <resource-list-component :resourceFramework="resourceFramework" :permissions="permissions" :currentIrr="irr" :resources="resources" :isIrrResourceList="isIrrResourceList" :num-resources-to-display="numResourcesToDisplay" :paginateHtml="paginateHtml" :csrf_token="csrf_token"></resource-list-component>
            </div>
          </div>
        </div>

        <!-- route == 'requestaccess' -->
        <div class="col-md-10" v-if="route == 'requestaccess'">
          <div class="container-fluid">
            <div class="row">
              <div class="col-md-12">
                <irr-access-request-data-component :route="route" :permissions="permissions" :irr="irr" :accessRequest="accessRequest"></irr-access-request-data-component>
              </div>
            </div>
          </div>
        </div>

        <!-- route == 'accessrequests' -->
        <div class="col-md-12" v-if="route == 'accessrequests'">
          <div class="container-fluid">
            <div class="row">
              <div class="col-md-12">
                <irr-access-request-list-component :route="route" :csrf_token="csrf_token" :permissions="permissions" :irr="irr" :accessRequests="accessRequests"></irr-access-request-list-component>
              </div>
            </div>
          </div>
        </div>

        <!-- route == 'publishrequests' -->
        <div class="col-md-12" v-if="route == 'publishrequests'">
          <div class="container-fluid">
            <div class="row">
              <div class="col-md-12">
                <irr-publish-request-list-component :route="route" :csrf_token="csrf_token" :permissions="permissions" :irr="irr" :publishRequests="publishRequests"></irr-publish-request-list-component>
              </div>
            </div>
          </div>
        </div>

        <!-- route == 'allpublishrequests' -->
        <div class="col-md-12" v-if="route == 'allpublishrequests'">
          <div class="container-fluid">
            <div class="row">
              <div class="col-md-12">
                <irr-publish-request-list-component :route="route" :csrf_token="csrf_token" :permissions="permissions" :publishRequests="publishRequests" :show-all-registries="true"></irr-publish-request-list-component>
              </div>
            </div>
          </div>
        </div>

        <!-- route == 'users' -->
        <div class="col-md-12" v-if="route == 'users'">
          <div class="container-fluid">
            <div class="row">
              <div class="col-md-12">
                <irr-user-list-component :route="route" :csrf_token="csrf_token" :permissions="permissions" :irr="irr" :users="users"></irr-user-list-component>
              </div>
            </div>
          </div>
        </div>

      </div>  <!-- row -->
    </div> <!-- container -->
  </div>
</template>

<script>
    export default {
      props: {
        irr: Object,
        route: String,
        permissions: Object,
        resources: Array,
        results: Object,
        paginateHtml: String,
        filter: String,
        irrInfo: Object,
        resourceFramework: Object,
        numUsers: Number,
        numResourcesToDisplay: Number,
        numPendingPublishRequests: Number,
        errors: Array,
        existingData: [Object, Array],
        accessRequest: [Object, Array],   //single request
        accessRequests: [Object, Array],  //multiple requests
        publishRequests: [Object, Array],
        users: [Object, Array],
        isIrrResourceList: {
          default: true,
          type: [Boolean, Number]
        },
        paginateHtml: String,
        specificUser: [Object, Array, Boolean],
        ownershipTransfer: {
          type: Object,
          default: () => ({ pending: null, incoming: null, error: '', status: '' })
        },
        // deactivation state and deactivate/activate/delete actions for the registry page - see IrrsController::lifecyclePayload()
        lifecycle: {
          type: Object,
          default: () => ({ isDeactivated: false, deactivatedAt: null, deactivatedByName: null, canDeactivate: false, canDelete: false, deactivateUrl: '', activateUrl: '', deleteUrl: '', numResources: 0, status: '', error: '' })
        },
        // flash message for the index page (e.g. after a registry was deleted)
        statusMessage: {
          type: String,
          default: ''
        },
      },
      data: function() {
        return {
          baseUrls: {
            resourceBaseUrl: '/resource/',
            irrBaseUrl: '/irr/',
            transferUrl: '/irr/transfer',
            userBaseUrl: '/user/'
          },
          csrf_token: $('meta[name="csrf-token"]').attr('content'),
          activeIrrId: 0,
        }
      },
      methods: {
        changeActiveIrr: function(id) {
          this.activeIrrId = id;
        },
      },
      mounted(){
        //
      }
    }
</script>
